import {
  Paintbrush,
  Eraser,
  Undo,
  Redo,
  Download,
  RotateCcw,
  Layers,
  Image as ImageIcon,
  Wand2,
  Plus,
  Minus,
  X,
} from 'lucide-react';
import useAdvancedEditorStore from '../../store/useAdvancedEditorStore';
import { applyMask, canvasToBlob, downloadBlob } from '../../utils/imageProcessing';
import ModelSelector from './ModelSelector';

/**
 * Barra de ferramentas principal
 *
 * Inclui:
 * - Ferramentas de edição (brush, eraser, magic wand)
 * - Controles de undo/redo
 * - Modos de visualização
 * - Exportação
 */
const AdvancedToolbar = ({ currentModel, onModelChange, isProcessing, onClose, onApply }) => {
  const {
    currentTool,
    setCurrentTool,
    undo,
    redo,
    canUndo,
    canRedo,
    reset,
    viewMode,
    setViewMode,
    originalImage,
    mask,
  } = useAdvancedEditorStore();

  /**
   * Handler para exportação da imagem
   */
  const handleExport = async () => {
    if (!originalImage || !mask) return;

    // Cria canvas temporário com a imagem original
    const canvas = document.createElement('canvas');
    canvas.width = originalImage.width;
    canvas.height = originalImage.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(originalImage, 0, 0);

    // Aplica máscara
    const resultCanvas = applyMask(canvas, mask);

    // Converte para blob e faz download
    const blob = await canvasToBlob(resultCanvas);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    downloadBlob(blob, `bg-removed-${timestamp}.png`);
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      <div className="px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Título */}
          <div className="flex items-center space-x-2">
            <Layers className="w-6 h-6 text-green-600" />
            <h1 className="text-xl font-bold text-gray-800">
              Editor Avançado
            </h1>
          </div>

          {/* Ferramentas principais */}
          <div className="flex items-center space-x-2">
            {/* Model Selector */}
            <ModelSelector
              currentModel={currentModel}
              onModelChange={onModelChange}
              isProcessing={isProcessing}
            />

            <div className="w-px h-8 bg-gray-300 mx-2" />

            {/* Eraser Add (Restaurar) */}
            <EraserButton
              mode="add"
              active={currentTool === 'eraser-add'}
              onClick={() => setCurrentTool('eraser-add')}
            />

            {/* Eraser Remove (Apagar) */}
            <EraserButton
              mode="remove"
              active={currentTool === 'eraser-remove'}
              onClick={() => setCurrentTool('eraser-remove')}
            />

            {/* Magic Wand Add */}
            <MagicWandButton
              mode="add"
              active={currentTool === 'magic-wand-add'}
              onClick={() => setCurrentTool('magic-wand-add')}
            />

            {/* Magic Wand Remove */}
            <MagicWandButton
              mode="remove"
              active={currentTool === 'magic-wand-remove'}
              onClick={() => setCurrentTool('magic-wand-remove')}
            />

            <div className="w-px h-8 bg-gray-300 mx-2" />

            {/* Undo */}
            <ToolButton
              icon={Undo}
              label="Desfazer"
              onClick={undo}
              disabled={!canUndo()}
            />

            {/* Redo */}
            <ToolButton
              icon={Redo}
              label="Refazer"
              onClick={redo}
              disabled={!canRedo()}
            />

            <div className="w-px h-8 bg-gray-300 mx-2" />

            {/* View Modes */}
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              <ViewModeButton
                icon={ImageIcon}
                label="Resultado"
                active={viewMode === 'result'}
                onClick={() => setViewMode('result')}
              />
              <ViewModeButton
                icon={Layers}
                label="Original"
                active={viewMode === 'original'}
                onClick={() => setViewMode('original')}
              />
            </div>

            <div className="w-px h-8 bg-gray-300 mx-2" />

            {/* Export */}
            <button
              onClick={handleExport}
              disabled={!originalImage || !mask}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-5 h-5" />
              <span className="font-medium">Baixar</span>
            </button>

            {/* Apply */}
            {onApply && (
              <button
                onClick={onApply}
                disabled={!originalImage || !mask}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span className="font-medium">Aplicar</span>
              </button>
            )}

            {/* Close */}
            {onClose && (
              <ToolButton
                icon={X}
                label="Fechar"
                onClick={onClose}
                variant="danger"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Botão de ferramenta
 */
const ToolButton = ({ icon: Icon, label, active, onClick, disabled, variant = 'default' }) => {
  const baseClasses = 'flex items-center justify-center w-10 h-10 rounded-lg transition-colors';
  const variantClasses = {
    default: active
      ? 'bg-green-100 text-green-600'
      : 'hover:bg-gray-100 text-gray-700',
    danger: 'hover:bg-red-100 text-red-600',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`${baseClasses} ${variantClasses[variant]} ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
};

/**
 * Botão de modo de visualização
 */
const ViewModeButton = ({ icon: Icon, label, active, onClick }) => {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`flex items-center justify-center w-8 h-8 rounded transition-colors ${
        active
          ? 'bg-white text-green-600 shadow-sm'
          : 'text-gray-600 hover:text-gray-800'
      }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
};

/**
 * Botão de Varinha Mágica com ícone sobreposto
 */
const MagicWandButton = ({ mode, active, onClick }) => {
  const label = mode === 'add' ? 'Varinha Mágica (Adicionar)' : 'Varinha Mágica (Remover)';
  const badgeColor = mode === 'add' ? 'bg-green-500' : 'bg-pink-500';
  const BadgeIcon = mode === 'add' ? Plus : Minus;

  return (
    <button
      onClick={onClick}
      title={label}
      className={`relative flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
        active
          ? 'bg-green-100 text-green-600'
          : 'hover:bg-gray-100 text-gray-700'
      }`}
    >
      <Wand2 className="w-5 h-5" />
      {/* Badge sobreposto */}
      <div className={`absolute -bottom-0.5 -right-0.5 ${badgeColor} rounded-full p-0.5`}>
        <BadgeIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} />
      </div>
    </button>
  );
};

/**
 * Botão de Borracha com ícone sobreposto (add/remove)
 */
const EraserButton = ({ mode, active, onClick }) => {
  const label = mode === 'add' ? 'Borracha (Restaurar)' : 'Borracha (Remover)';
  const badgeColor = mode === 'add' ? 'bg-green-500' : 'bg-pink-500';
  const BadgeIcon = mode === 'add' ? Plus : Minus;

  return (
    <button
      onClick={onClick}
      title={label}
      className={`relative flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
        active
          ? 'bg-green-100 text-green-600'
          : 'hover:bg-gray-100 text-gray-700'
      }`}
    >
      <Eraser className="w-5 h-5" />
      {/* Badge sobreposto */}
      <div className={`absolute -bottom-0.5 -right-0.5 ${badgeColor} rounded-full p-0.5`}>
        <BadgeIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} />
      </div>
    </button>
  );
};

export default AdvancedToolbar;
